import React, { useRef, useEffect } from 'react'
import {
  Box,
  Text,
  useResponsiveWrapperContext,
} from '@usap/uicomponents'

import commaSeparetedValue from '../../../../../helpers/commaSeparetedValue'
import Image from './Image'
import Details from './Details'
import Quantity from './Quantity'
import VehicleFitment from './VehicleFitment'
import Warranty from './Warranty'
import RemoveFromCart from './RemoveFromCart'
import DeliveryDates from './Details/DeliveryDates'
import FreeShippingBadge from './FreeShippingBadge'
import InstallAtShopWrapperCart from '../../../../DIFM/InstallAtShop/InstallAtShopWrapperCart'
import useHandleAddVehicle from './VehicleFitment/hooks/useHandleAddVehicle'
import useWmoEnabled from '../../../../../hooks/useWmoEnabled'
import ItemDesktopWmo from './ItemDesktopWmo'
import ItemMobileWmo from './ItemMobileWmo'
import { GTM } from '../../../../../helpers'
import formatItemsGroundDeliveryTime from '../../Summary/ProceedToCheckout/Regular/formatItemsGroundDeliveryTime'
import useIsPromoBadgeEnabled from '../../../../../hooks/useIsPromoBadgeEnabled'
import trackProdVisSKUAdded from '../../../logging/gtm/trackProdVisSKUAdded'
import { shouldShowFitment } from '../../../hooks/useBrUniversalParts'
import { useUniversalProductNoFitment } from '../../../../../hooks/useUniversalProductNoFitment'
import { useBloomreach } from '../../../../../contexts/br.context'
import { usePowersports } from '../../../../../hooks/usePowersports'

const Item = props => {
  const {
    data, index, tooltip, lastSkuEta, promoBadgeConfig, showLoader, zipCode,
  } = props

  const { isDesktop } = useResponsiveWrapperContext()
  const isWmoEnabled = useWmoEnabled()
  const isPromoBadgeEnabled = useIsPromoBadgeEnabled()
  const { brUniversalParts = null } = useBloomreach() || {}

  const { selectItem } = GTM
  const shouldDisplayCpp = true
  let hasWarrantyOption = false

  const { product } = data || {}

  const isUniversalNoFitmentEnabled = useUniversalProductNoFitment()
  const isPowersportsEnabled = usePowersports()
  const isFitmentVisible = isPowersportsEnabled && isUniversalNoFitmentEnabled && product?.isUniversal ? shouldShowFitment(product?.part?.name || product?.part, brUniversalParts) : true

  if (data.product && data.product.attributes) {
    hasWarrantyOption = data.product.attributes
      .find(attribute => attribute.label.toLowerCase() === 'warranty')
  }

  const itemTotal = parseFloat(data.price.regular * data.modifiers.quantity).toFixed(2) || 0

  const handleAddVehicle = useHandleAddVehicle({
    product: data.product,
  })

  const isTrackEppImpressionImpressionFired = useRef(false)
  useEffect(() => {
    if (!isTrackEppImpressionImpressionFired.current && data?.referrer === 'igarage') {
      let ymms = ' '

      if (typeof data.modifiers.vehicle.details.year !== 'undefined' &&
        typeof data.modifiers.vehicle.details.make !== 'undefined' &&
        typeof data.modifiers.vehicle.details.model !== 'undefined'
      ) {
        ymms = `${data?.modifiers?.vehicle?.details?.year || ''} ${data?.modifiers?.vehicle?.details?.make || ''} ${data?.modifiers?.vehicle?.details?.model || ''}  ${data.modifiers.vehicle.details.submodel || ''}`.trim()
      }

      trackProdVisSKUAdded(
        data.product.sku,
        data.product.part.name,
        ymms,
        data.product.price.regular,
        data.referrer
      )
      isTrackEppImpressionImpressionFired.current = true
    }
  }, [data])

  const trackSelectItem = () => {
    let { product: item, price, modifiers, inventory } = data
    const { vehicle, quantity: itemQty } = modifiers
    const { details } = vehicle || {}
    const { quantity, shipping } = inventory
    const itemAdded = (parseInt(quantity) === 0) ? 0 : itemQty
    const itemsGroundDeliveryTime = formatItemsGroundDeliveryTime([data])
    const {
      productId,
      brand: { name: brandName } = {},
    } = item

    item.id = productId
    item.brand = brandName
    item.price = price?.regular
    item.itemAdded = itemAdded
    item.quantity = quantity
    item.shipping = shipping
    item.deliveryDate = itemsGroundDeliveryTime
    item.vehicleData = details
    item.itemListName = 'Cart Item List'
    selectItem(item, 'CartItem', index)
  }

  const showCpp = shouldDisplayCpp && hasWarrantyOption && data.warranty
  if (isWmoEnabled) {
    const propsWmo = {
      showCpp,
      handleAddVehicle,
      itemTotal,
      data,
      index,
      tooltip,
      lastSkuEta,
      trackSelectItem,
      promoBadgeConfig,
      isPromoBadgeEnabled,
      showLoader,
      zipCode,
      isFitmentVisible,
    }
    if (isDesktop) {
      return <ItemDesktopWmo {...propsWmo} />
    }
    return <ItemMobileWmo {...propsWmo} />
  }

  return (
    <>
      {isDesktop === false && (
        <Box
          border={{ side: 'all', color: 'light-3' }}
          round={{ size: 'xsmall', corner: 'top' }}
          background="white"
          pad="8px"
          style={{ borderBottomWidth: '0px' }}
        >
          <FreeShippingBadge data={data} />
          <DeliveryDates data={data} dataTestId="wom-see-all-dates-cart" zipCode={zipCode} />
        </Box>
      )}
      <Box
        pad="medium"
        margin={{ bottom: 'small' }}
        round={
          isDesktop && index === 0
            ? { size: 'xsmall', corner: 'bottom' }
            : 'xsmall'
        }
        border={{ side: 'all', color: 'light-3' }}
        background="white"
        data-testid={`cart-item-${data?.product?.sku}`}
      >
        <Box direction="row">
          <Box width={isDesktop ? '55%' : '100%'}>
            {isDesktop && (
              <Text size="xsmall" weight={500} color="dark-1">
                ITEM
              </Text>
            )}
            <Box direction="row" gap="small">
              <Image data={data} />
              <Details data={data} index={index} />
            </Box>
          </Box>

          {isDesktop === true && (
            <Box width="10%" margin={{ horizontal: 'medium' }}>
              <Quantity data={data} index={index} />
            </Box>
          )}

          {isDesktop === true && (
            <Box width="25%" margin={{ horizontal: 'large' }}>
              <Text size="xsmall" weight="bold" color="dark-1">
                ITEM TOTAL
              </Text>
              <Text size="small" weight="bold" margin={{ top: 'medium' }}>
                $
                {commaSeparetedValue(itemTotal)}
              </Text>
            </Box>
          )}

          {isDesktop === true && (
            <Box width="10%">
              <Text size="xsmall" textAlign="end" weight="bold" color="dark-1">
                REMOVE
              </Text>
              <RemoveFromCart data={data} index={index} />
            </Box>
          )}
        </Box>

        {isDesktop === true && (
          <Box
            width={isDesktop ? '85%' : '100%'}
            alignSelf={isDesktop ? 'end' : 'start'}
          >
            <VehicleFitment data={data} />
          </Box>
        )}

        {showCpp && (
          <Box
            width={isDesktop ? '85%' : '100%'}
            alignSelf={isDesktop ? 'end' : 'start'}
          >
            <Warranty data={data} tooltip={tooltip} />
          </Box>
        )}

        {isDesktop === false && (
          <Box
            width={isDesktop ? '85%' : '100%'}
            alignSelf={isDesktop ? 'end' : 'start'}
          >
            <VehicleFitment data={data} />
          </Box>
        )}

        {isDesktop === false && (
          <Box
            direction="row"
            justify="between"
            align="center"
            style={{ paddingTop: '12px' }}
          >
            <Quantity data={data} index={index} />
            <Box
              align="right"
              justify="end"
            >
              <Text size="medium" color="dark-2" weight="bold">
                $
                {commaSeparetedValue(itemTotal)}
              </Text>
            </Box>
          </Box>
        )}

        <Box
          width={isDesktop ? '85%' : '100%'}
          alignSelf={isDesktop ? 'end' : 'start'}
          margin={{ top: isDesktop ? '0' : 'medium' }}
        >
          <InstallAtShopWrapperCart
            cartItemData={data}
            addVehicleTrigger={handleAddVehicle}
            lastSkuEta={lastSkuEta}
          />
        </Box>
      </Box>
    </>
  )
}

export default Item
